/* The step panel: what one step of a run did, to the point. A sentence and four figures
   first, then what the step took in and gave back as cards (`stepBodies`), the steps either
   side of it, its details, and its span as the trace holds it (Raw JSON). */
import React, { useMemo, useRef, useState } from "react";
import { Braces, ChevronLeft, ChevronRight, StickyNote, X } from "lucide-react";

import Fold from "@/shared/components/Fold";
import { ListCard } from "@/shared/components/ListCard";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { StatTile } from "@/shared/components/StatTile";
import { CopyButton } from "@/shared/components/markdown/CodeBlock";
import { plural } from "@/shared/components/teams-ui";
import { BrandButton, IconButton } from "@/shared/components/ui-kit";
import { agentMood } from "@/shared/lib/agent-mood";
import { cn } from "@/shared/lib/cn";
import { interfaceName } from "@/shared/lib/interface-catalog";
import { fmtMs, fmtTokens } from "@/shared/lib/runs";
import { kindOf } from "@/shared/lib/trace-kinds";
import { callerOf, callsOf, contextOf, errorReadBy, previousCalls, purposeOf, stepsOf, wasStopped } from "../traceModel";
import { chipCls, KindIcon, TierChip } from "./atoms";
import { exactUsd, money } from "./money";
import { JsonView } from "./payloads";
import { AttemptCards, Fields, ModelCards, OtherCards, RunCards, ToolCards, WaitCards } from "./stepBodies";

import type { Agent, Run } from "@/shared/types";
import type { AssistantPayload, LlmInputPayload, Span, SpanNote, ToolInputPayload } from "../types";
import type { PanelPrefs, StepContext, TraceOverview } from "./stepBodies";

const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A wait on a person the trace recorded, as against a fixture's approval step. */
const isWait = (span: Span) => span.kind === "approval" && !!span.record;

/** What kind of step it is, in words. */
function kindWord(span: Span): string {
  if (span.kind === "agent") return "Run";
  if (span.kind === "turn") return "Attempt";
  if (span.kind === "llm") return "Model call";
  if (span.kind === "tool") return "Tool call";
  if (isWait(span)) return span.name === "approval" ? "Waiting for approval" : "Waiting for an answer";
  return kindOf(span.kind).label;
}

const RUN_STATE: Record<string, [string, string]> = {
  success: ["Done", "text-[var(--success)]"], error: ["Failed", "text-[var(--danger)]"],
  running: ["Running", "text-[var(--info-text)]"], queued: ["Queued", "text-[var(--info-text)]"],
  // the person waited on may be the builder or the agent's own customer
  attention: ["Waiting", "text-[var(--warn-text)]"], halted: ["Stopped", "text-[var(--text-secondary)]"],
};

/** How the step stands. The run's own status speaks for the root. */
function stateOf(span: Span, run: Run): [string, string] {
  if (span.kind === "agent") return RUN_STATE[run.status] ?? RUN_STATE.success;
  if (span.status === "error") return RUN_STATE.error;
  if (wasStopped(span)) return RUN_STATE.halted;
  // a recorded step still open is running, or waiting on a person; a fixture's waits on approval
  if (span.status === "pending") return isWait(span) || !run.recorded ? RUN_STATE.attention : RUN_STATE.running;
  return RUN_STATE.success;
}

/** How the run ended, as the start of its sentence. */
const RUN_SENTENCE: Record<string, string> = {
  running: "Working", queued: "Queued", attention: "Waiting on a person", halted: "Stopped", error: "Failed", success: "Answered",
};

/** One plain sentence on what the step did, from what was recorded and nothing else. */
function sentenceOf(span: Span, ctx: StepContext): string | null {
  const a = ctx.overview.analytics;
  if (span.kind === "agent") {
    const channel = interfaceName(ctx.run.recorded?.interface ?? "platform");
    const work = `${plural(a.llmCount, "model call")} and ${plural(a.toolCount, "tool call")}`;
    const live = ctx.run.status === "running" || ctx.run.status === "queued";
    return `${RUN_SENTENCE[ctx.run.status] ?? "Ended"} through ${channel}, ${live ? "so far" : "after"} ${work}.`;
  }
  if (wasStopped(span)) return "The run was stopped before this step finished.";
  if (isWait(span)) {
    const asked = span.name === "approval" ? "The run asked a person to approve a tool call" : "The run asked a person a question";
    return span.status === "pending" ? `${asked} and is waiting.` : `${asked}; the answer came after ${fmtMs(span.dur)}.`;
  }
  if (span.kind === "llm") {
    if (span.status === "error") return "The model call failed.";
    const context = contextOf(span, ctx.previousCall(span));
    const read = context.first
      ? `read the prompt (${plural(context.length, "message")})`
      : context.fresh.length ? `read ${plural(context.fresh.length, "new message")}` : "read nothing new";
    if (span.status === "pending") return `${sentenceCase(read)} and is writing its reply.`;
    const calls = callsOf(span).map((c) => c.name);
    return `${sentenceCase(read)} and ${calls.length ? `called ${calls.join(", ")}` : "wrote the answer"}.`;
  }
  if (span.kind === "tool") {
    const what = purposeOf(span) ?? `Ran ${span.name}`;
    if (span.status === "error") return `${what}. It failed${errorReadBy(span, ctx.steps) ? ", and the model read the error in its next call" : ""}.`;
    if (span.status === "pending") return `${what}. Still running.`;
    return `${what}.`;
  }
  return span.label || null;
}

/** The four figures under the sentence: label, value, and the value's tone. */
function factsOf(span: Span, ctx: StepContext): [string, string, string?][] {
  const { analytics, idle } = ctx.overview;
  if (span.kind === "agent") {
    // `runs.cost_usd` is written when the run pauses or ends, 0 before; until then the model calls' sum
    const charged = ctx.run.recorded?.cost_usd || analytics.cost;
    return [
      ["Duration", fmtMs(span.dur)], ["Cost", money(charged)], ["Steps", String(analytics.llmCount + analytics.toolCount)],
      ["Idle", fmtMs(idle), idle > 0 ? "text-[var(--warn-text)]" : undefined],
    ];
  }
  if (span.kind === "llm") {
    const usage = (span.io.output as AssistantPayload | null)?.usage;
    return [
      ["Duration", fmtMs(span.dur)],
      ["Tokens", usage ? `${fmtTokens(usage.input_tokens ?? 0)} → ${fmtTokens(usage.output_tokens ?? 0)}` : "—"],
      ["Cached", usage?.cache_read_input_tokens ? fmtTokens(usage.cache_read_input_tokens) : "—"],
      ["Cost", span.cost != null ? money(span.cost) : "—"],
    ];
  }
  const [state, tone] = stateOf(span, ctx.run);
  const cost = span.cost ?? span.record?.cost_usd;
  return [["Duration", fmtMs(span.dur)], ["Started", `+${fmtMs(span.start)}`], ["Status", state, tone], ["Cost", cost ? money(cost) : "—"]];
}

/* ------------------------------ steps either side ------------------------------ */

const stepName = (s: Span) => (s.kind === "llm" ? s.chip || "Model call" : s.name);
const stepLine = (s: Span) => (s.kind === "tool" && purposeOf(s)) || s.label || kindWord(s);

function StepLink({ step, label, end, onSelect }: { step: Span | null; label: string; end: "start" | "end"; onSelect: (id: string) => void }) {
  const next = end === "end";
  return (
    <button
      type="button"
      disabled={!step}
      onClick={() => step && onSelect(step.id)}
      className={cn(
        "press min-w-0 px-3 pt-2 pb-2.5 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift hover:bg-[var(--surface-sunk)] disabled:opacity-50 disabled:pointer-events-none",
        next ? "text-right" : "text-left",
      )}
    >
      <span className={cn("flex items-center gap-1 text-[11.5px] text-[var(--text-tertiary)]", next && "justify-end")}>
        {!next && <ChevronLeft className="w-3 h-3" strokeWidth={1.75} />}
        {label}
        {next && <ChevronRight className="w-3 h-3" strokeWidth={1.75} />}
      </span>
      <span className="block mt-0.5 truncate font-mono text-[12.5px] font-medium text-[var(--text-primary)]">{step ? stepName(step) : "—"}</span>
      <span className="block truncate text-[12px] text-[var(--text-tertiary)]">{step ? stepLine(step) : next ? "End of the run" : "Start of the run"}</span>
    </button>
  );
}

/** The step before (a tool's caller, a model call's previous call) and the one after. */
function StepLinks({ span, ctx }: { span: Span; ctx: StepContext }) {
  const i = ctx.steps.indexOf(span);
  const caller = span.kind === "tool" ? callerOf(span, ctx.steps) : null;
  const previous = span.kind === "llm" ? ctx.previousCall(span) : null;
  const before = caller ?? previous ?? (i > 0 ? ctx.steps[i - 1] : null);
  const after = i >= 0 && i < ctx.steps.length - 1 ? ctx.steps[i + 1] : null;
  return (
    <div className="grid grid-cols-2 gap-1 p-1 rounded-[14px] bg-[var(--surface-2)]">
      <StepLink step={before} label={caller ? "Called by" : previous ? "Previous call" : "Before"} end="start" onSelect={ctx.onSelect} />
      <StepLink step={after} label="Next" end="end" onSelect={ctx.onSelect} />
    </div>
  );
}

/* --------------------------------- details --------------------------------- */

const clock = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "medium" }) : null);

function detailsOf(span: Span, ctx: StepContext): Record<string, string | null | undefined> {
  if (span.kind === "agent") {
    const rec = ctx.run.recorded;
    const modelCost = ctx.overview.analytics.cost;
    return {
      "Trace ID": ctx.run.id, Channel: rec ? interfaceName(rec.interface) : null, Status: ctx.run.status,
      Started: clock(rec?.started_at), Ended: clock(rec?.ended_at), Attempts: rec?.attempt != null ? String(rec.attempt) : null,
      "Model cost": modelCost ? exactUsd(modelCost) : null,
      "Tools available": span.meta.tools?.length ? span.meta.tools.join(", ") : null,
    };
  }
  const common = {
    "Step ID": span.meta.observation_id, Started: clock(span.record?.started_at) ?? `+${fmtMs(span.start)}`,
    Offset: `+${fmtMs(span.start)} → +${fmtMs(span.start + span.dur)}`, "Recorded as": span.record?.status,
    "Status message": span.meta.status_message,
  };
  if (span.kind === "llm") {
    const input = span.io.input as LlmInputPayload | null;
    const output = span.io.output as AssistantPayload | null;
    const usage = output?.usage;
    return {
      Model: span.chip, Tier: span.tier, "Stop reason": output?.stop_reason,
      "First token": span.ttft != null ? fmtMs(span.ttft) : null,
      Tokens: usage ? `${(usage.input_tokens ?? 0).toLocaleString()} in · ${(usage.output_tokens ?? 0).toLocaleString()} out · ${(usage.cache_read_input_tokens ?? 0).toLocaleString()} cached` : null,
      Cost: span.cost != null ? exactUsd(span.cost) : null,
      Context: input ? `${plural(contextOf(span, ctx.previousCall(span)).length, "message")} · ${plural(input.tools.length, "tool")} offered` : null,
      ...common,
    };
  }
  if (span.kind === "tool") {
    const input = span.io.input as ToolInputPayload | null;
    return { Tool: span.raw?.tool ?? span.name, App: span.chip, "Tool call ID": input?.tool_use_id, ...common };
  }
  return { ...common, Cost: span.record?.cost_usd ? exactUsd(span.record.cost_usd) : null };
}

function Details({ rows }: { rows: Record<string, string | null | undefined> }) {
  const [open, setOpen] = useState(false);
  const shown = Object.fromEntries(Object.entries(rows).filter(([, v]) => v != null && v !== ""));
  return (
    <ListCard label="Details" folded={!open} onToggle={() => setOpen((v) => !v)}>
      <Fields data={shown} />
    </ListCard>
  );
}

/* ---------------------------------- notes ---------------------------------- */

function Notes({ notes, onAdd, onDelete }: { notes: SpanNote[]; onAdd: (text: string) => void; onDelete: (id: string) => void }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd(text);
    setDraft("");
  };
  return (
    <div className="mt-3 space-y-1.5">
      {notes.map((n) => (
        <div key={n.id} className="group flex items-start gap-2 rounded-[10px] bg-[var(--surface-2)] px-3 py-2">
          <span className="flex-1 min-w-0 text-[12.5px] leading-[1.55] text-[var(--text-primary)] whitespace-pre-wrap break-words">{n.text}</span>
          <button
            type="button"
            onClick={() => onDelete(n.id)}
            title="Delete note"
            aria-label="Delete note"
            className="press shrink-0 w-5 h-5 rounded-[5px] flex items-center justify-center text-[var(--text-quaternary)] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-[var(--danger)]"
          >
            <X className="w-3 h-3" strokeWidth={1.75} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Pin a note to this step…"
          aria-label="Note"
          className="flex-1 min-w-0 h-8 px-3 rounded-[9px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[12.5px] text-[var(--text-primary)] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)]"
        />
        <BrandButton onClick={add} disabled={!draft.trim()} className="disabled:opacity-40 disabled:pointer-events-none">Add</BrandButton>
      </div>
    </div>
  );
}

/* ---------------------------------- panel ---------------------------------- */

export default function StepPanel({ span, root, agent, run, overview, notes, onAddNote, onDeleteNote, onSelect, prefs, onPrefs }: {
  span: Span;
  root: Span;
  agent: Agent;
  run: Run;
  overview: TraceOverview;
  notes: Record<string, SpanNote[]>;
  onAddNote: (spanId: string, text: string) => void;
  onDeleteNote: (spanId: string, noteId: string) => void;
  onSelect: (id: string) => void;
  /** Fields or JSON, Raw JSON open: kept by the trace, so they hold from step to step. */
  prefs: PanelPrefs;
  onPrefs: (change: Partial<PanelPrefs>) => void;
}) {
  const steps = useMemo(() => stepsOf(root), [root]);
  const before = useMemo(() => previousCalls(root), [root]);
  const ctx: StepContext = { run, overview, steps, previousCall: (s) => before.get(s.id) ?? null, onSelect, prefs, onPrefs };
  const spanNotes = notes[span.id] ?? [];
  // a step with notes opens on them
  const [notesOpen, setNotesOpen] = useState(spanNotes.length > 0);
  const rawRef = useRef<HTMLDivElement>(null);
  // drawn from the first time it opens, so closing it folds the tree away rather than dropping it
  const [rawDrawn, setRawDrawn] = useState(prefs.raw);
  if (prefs.raw && !rawDrawn) setRawDrawn(true);
  const [state, tone] = stateOf(span, run);
  const sentence = sentenceOf(span, ctx);
  // the root's own record is the run, with its attempts' spans; a fixture step has none, so its built step stands in
  const raw = span.kind === "agent"
    ? { run: run.recorded ?? null, attempts: span.attempts ?? [] }
    : span.record ?? { name: span.name, kind: span.kind, input: span.io.input, output: span.io.output, meta: span.meta };
  const showRaw = () => {
    onPrefs({ raw: true });
    setTimeout(() => rawRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 420);
  };

  return (
    <aside aria-label="Step details" className="w-full h-full min-w-0 flex flex-col min-h-0">
      <div className="shrink-0 px-[18px] pt-4 pb-3.5 border-b hairline">
        <div className="flex items-center gap-2.5 min-w-0">
          {span.kind === "agent" ? (
            <PixelAvatar seed={agent.seed} hue={agent.hue} size={30} ring rounded={9} emotion={agentMood(agent)} />
          ) : (
            <KindIcon kind={span.kind} status={span.status} tool={span.raw?.tool} size={30} />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={cn("min-w-0 truncate text-[var(--text-primary)]", span.kind === "agent" ? "text-[15px] font-medium tracking-[-0.01em]" : "font-mono text-[14px] font-medium")}>
                {span.kind === "agent" ? span.name : stepName(span)}
              </span>
              {span.kind !== "llm" && span.chip && <span className={chipCls}>{span.chip}</span>}
              {span.tier && <TierChip tier={span.tier} />}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[12px]">
              <span className={cn("inline-flex items-center gap-1.5 font-medium", tone)}><span className="w-1.5 h-1.5 rounded-full bg-current" />{state}</span>
              <span className="text-[var(--text-tertiary)]">{kindWord(span)}</span>
            </div>
          </div>
          <IconButton active={notesOpen} onClick={() => setNotesOpen((v) => !v)} title="Notes on this step" aria-label={`Notes on this step${spanNotes.length ? `: ${spanNotes.length}` : ""}`} className="relative">
            <StickyNote className="w-3.5 h-3.5" strokeWidth={1.75} />
            {spanNotes.length > 0 && (
              <span aria-hidden="true" className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--brand)] text-[var(--brand-foreground)] font-mono text-[10px] font-semibold tabular-nums flex items-center justify-center">
                {spanNotes.length}
              </span>
            )}
          </IconButton>
          <IconButton onClick={showRaw} title="Raw JSON" aria-label="Show the raw JSON">
            <Braces className="w-3.5 h-3.5" strokeWidth={1.75} />
          </IconButton>
        </div>
        {sentence && <p className="m-0 mt-2.5 text-[13.5px] leading-[1.55] text-[var(--text-secondary)] break-words">{sentence}</p>}
        <Fold open={notesOpen}>
          <Notes notes={spanNotes} onAdd={(text) => onAddNote(span.id, text)} onDelete={(id) => onDeleteNote(span.id, id)} />
        </Fold>
      </div>
      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col gap-2.5 p-3.5 pb-8">
          {/* the page's stat tray, two by two so a figure never clips in a narrow panel */}
          <div className="grid grid-cols-2 gap-1 p-1 rounded-[18px] bg-[var(--surface-2)]">
            {factsOf(span, ctx).map(([label, value, valueTone]) => (
              // figures in mono, as every number is; a status reads as words
              <StatTile key={label} label={label} value={<span className={cn(/^[\d$+—]/.test(value) && "font-mono", valueTone)}>{value}</span>} />
            ))}
          </div>
          {span.kind === "agent" ? <RunCards span={span} ctx={ctx} />
            : span.kind === "turn" ? <AttemptCards span={span} />
            : span.kind === "llm" ? <ModelCards span={span} ctx={ctx} />
            : span.kind === "tool" ? <ToolCards span={span} ctx={ctx} />
            : isWait(span) ? <WaitCards span={span} ctx={ctx} />
            : <OtherCards span={span} ctx={ctx} />}
          {span.kind !== "agent" && span.kind !== "turn" && <StepLinks span={span} ctx={ctx} />}
          <Details rows={detailsOf(span, ctx)} />
          <div ref={rawRef}>
            {/* the fold's strip is one button, so Copy sits in the card; the tree is drawn only
                once opened, since a first model call's span carries every tool's schema */}
            <ListCard label={span.kind === "agent" ? "Run record" : "Raw JSON"} folded={!prefs.raw} onToggle={() => onPrefs({ raw: !prefs.raw })}>
              {rawDrawn && (
                <div className="relative px-4 py-3 pr-11">
                  <div className="absolute top-1.5 right-1.5"><CopyButton text={JSON.stringify(raw, null, 2)} label="Copy raw JSON" /></div>
                  <JsonView data={raw} full />
                </div>
              )}
            </ListCard>
          </div>
        </div>
      </div>
    </aside>
  );
}
